import { LocalDate, Month } from "@js-joda/core";
import React from "react";
import { CommCenterTicketCreateParams } from "../../activities/AppNavigator";
import { Page } from "../../activities/AppPage";
import { API } from "../../API";
import useCaregiver from "../../hooks/useCaregiver";
import useClockedInStatus from "../../hooks/useClockedInStatus";
import useMixin from "../../hooks/useMixin";
import usePatientsState from "../../hooks/usePatientsState";
import { useUploadVerificationSelfieMutation } from "../../hooks/useUploadVerificationSelfieMutation";
import { isLoading } from "../../Loading";
import {
  ClockInOutCallType,
  VisitInstanceClockinClockoutRequestParams,
  VisitInstanceId,
} from "../../messages";
import { STRINGS } from "../../strings";
import { AppState } from "../../system/AppState";
import { translate } from "../../translate";
import { getIsRN } from "../../utils/caregiverUtils";
import { decreaseScheduleByMonth, increaseScheduleByMonth } from "../../utils/handleScheduleDates";
import { getTasksWithRequiredFixes } from "../../utils/taskUtils";
import { CicoModalEntity } from "../../utils/visitUtils";
import LoadingScaffold from "../LoadingScaffold";
import useContactSupport from "../useContactSupport";
import ScheduledVisitsRNScreen from "./ScheduledVisitsRNScreen";
import ScheduledVisitsScreen from "./ScheduledVisitsScreen";
import { PatientTaskInstance } from "../../system/AppState/AppTasksState";

type Props = {
  api: API;
  appState: AppState;
  scheduledVisitsParams?: {
    isApplyOnlyTasksWithRequiredFixesFilter?: boolean;
  };
  onClock: (
    visitInstanceId: VisitInstanceId,
    clockInOutCallType: ClockInOutCallType,
    visitInstanceClockinClockoutRequestParams: VisitInstanceClockinClockoutRequestParams
  ) => Promise<boolean>;
  onClickNewChatWithMedflyt: (params: CommCenterTicketCreateParams) => void;
  onClickStartNow: (entity: CicoModalEntity) => void;
  onRequestNavigate: (page: Page) => void;
  onClickGoBack: () => void;
};

/**
 * Sets the initial year and month for the calendar if tasks with required fixes exist
 * @param yearMonth - The year/month state object with set method
 * @param appTasksState - The tasks state
 * @param isApplyFilter - Whether to apply the filter for required fixes
 * @returns Whether the year/month was updated
 */
function getYearMonthToMostRecentTaskWithRequiredFixes(
  tasks: PatientTaskInstance[]
): { year: number; month: number } | null {
  const tasksWithRequiredFixes = getTasksWithRequiredFixes(tasks);

  // Set year and month to the most recent task with required fixes
  if (tasksWithRequiredFixes.length > 0) {
    const mostRecentTask = tasksWithRequiredFixes[0];
    return {
      year: mostRecentTask.year,
      month: mostRecentTask.month,
    };
  }

  return null;
}

export default function ScheduledVisitsActivity(props: Props) {
  const yearMonth = useYearMonth();
  const caregiver = useCaregiver(props.appState);
  const clockedInStatus = useClockedInStatus(props.appState);
  const mixin = useMixin(props.appState);
  const patientsState = usePatientsState(props.appState);
  const contactSupport = useContactSupport({
    api: props.api,
    appState: props.appState,
    onRequestNavigate: props.onRequestNavigate,
    onClickNewChatWithMedflyt: props.onClickNewChatWithMedflyt,
  });

  const uploadVerificationSelfie = useUploadVerificationSelfieMutation({ api: props.api });

  const hasInitialized = React.useRef(false);

  if (
    isLoading(caregiver) ||
    isLoading(clockedInStatus) ||
    isLoading(props.appState.appTasksState) ||
    isLoading(props.appState.scheduledVisits) ||
    isLoading(mixin.appVisitEditRequestsState) ||
    isLoading(contactSupport)
  ) {
    return (
      <LoadingScaffold title={translate(STRINGS.MY_VISITS)} onClickGoBack={props.onClickGoBack} />
    );
  }

  // This ensures we only try to find the most recent task with fixes once,
  if (
    !hasInitialized.current &&
    props.scheduledVisitsParams?.isApplyOnlyTasksWithRequiredFixesFilter === true
  ) {
    const mostRecentTaskWithFixesDates = getYearMonthToMostRecentTaskWithRequiredFixes(
      props.appState.appTasksState.tasks.toArray()
    );
    if (mostRecentTaskWithFixesDates) {
      yearMonth.set(mostRecentTaskWithFixesDates.year, mostRecentTaskWithFixesDates.month);
    }
    hasInitialized.current = true;
  }

  const isRN = getIsRN(caregiver.certifications);

  const screenProps = {
    scheduleYear: yearMonth.scheduleYear,
    scheduleMonth: yearMonth.scheduleMonth,
    caregiver: caregiver,
    clockedInStatus: clockedInStatus,
    patientTasks: props.appState.appTasksState.tasks,
    contactSupportProps: contactSupport,
    importedVisits: props.appState.scheduledVisits.importedVisitDetails,
    rescheduleRequests: mixin.appVisitEditRequestsState.visitEditRequests,
    visitInstances: props.appState.scheduledVisits.scheduledVisitDetails,
    onChangeYearMonth: yearMonth.set,
    onClickPreviousMonth: yearMonth.previous,
    onClickNextMonth: yearMonth.next,
    onClickGoBack: props.onClickGoBack,
    onRequestAvailableVisits: props.onClickGoBack,
    onClickStartNow: props.onClickStartNow,
    onRequestNavigate: props.onRequestNavigate,
    onClock: props.onClock,
    onUploadVerificationSelfie: uploadVerificationSelfie.mutateAsync,
    onRequestVisitAdjustmentCheck: props.api.getCaregiverCanAdjustVisitInstanceTimes,
    onCheckIfDocumentsSubmitted: props.api.checkIfAllDocumentsSubmitted,
    onCaregiverMarkVisitAsMissed: props.api.caregiverMarkVisitAsMissed,
    scheduledVisitsParams: props.scheduledVisitsParams,
  };

  return isRN ? (
    <ScheduledVisitsRNScreen patientsState={patientsState} {...screenProps} />
  ) : (
    <ScheduledVisitsScreen {...screenProps} />
  );
}

function useYearMonth() {
  const [scheduleYear, setScheduleYear] = React.useState(LocalDate.now().year());
  const [scheduleMonth, setScheduleMonth] = React.useState(LocalDate.now().month());

  const previous = () => {
    return decreaseScheduleByMonth(scheduleMonth, scheduleYear, setScheduleMonth, setScheduleYear);
  };

  const next = () => {
    return increaseScheduleByMonth(scheduleMonth, scheduleYear, setScheduleMonth, setScheduleYear);
  };

  const set = (year: number, month: number) => {
    setScheduleYear(year);
    setScheduleMonth(Month.of(month));
  };

  return { scheduleYear, scheduleMonth, previous, next, set };
}
